/**
 * =============================================================================
 * METRIC CARD COMPONENT - AI-NATIVE DESIGN SYSTEM
 * =============================================================================
 *
 * Displays a single KPI metric with prominent value display.
 *
 * DESIGN SYSTEM:
 * - KPI numbers: 32-36px bold
 * - Label: 12-13px uppercase, muted
 * - 8pt spacing grid
 * - No decorative borders - subtle container styling
 * - Semantic colors for trends only
 *
 * DATA:
 * - Driven entirely by MetricCardInstance config + optional context.
 * - No fetching or computation logic in this file.
 * - All data resolution handled by useMetricCard → compute-engine.
 */

import { ActionIcon, Box, Group, Skeleton, Stack, Text, Tooltip } from '@mantine/core';
import {
    ChatCircleDots,
    Minus,
    PencilSimple,
    TrendDown,
    TrendUp,
    Warning,
} from '@phosphor-icons/react';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { currencySymbol, formatCount } from '@utils/currency';
import type { MetricCardInstance } from '@diagon-alley/common';
import { useMetricCard } from '../../../../hooks/useMetricCard';
import { useWidgetContextCapture } from '@hooks/useWidgetContextCapture';
import { EditDialog } from '../../shared/EditDialog';
import type { EditDialogConfig, MetricTrend } from '../../types';

// =============================================================================
// DESIGN TOKENS
// =============================================================================
const spacing = {
    xs: 8,
    sm: 16,
    md: 24,
} as const;

const semanticColors = {
    positive: 'rgb(45, 187, 136)',
    negative: 'rgb(205, 101, 101)',
    neutral: 'rgb(155, 155, 155)',
} as const;

// =============================================================================
// HELPER: Format metric value
// =============================================================================
const formatValue = (
    value: number | null,
    format: MetricCardInstance['format'],
): string => {
    if (value === null || value === undefined || isNaN(value)) return '—';

    let formatted: string;

    if (format.compact) {
        if (value >= 1_000_000) {
            formatted = `${(value / 1_000_000).toFixed(format.decimals ?? 1)}M`;
        } else if (value >= 1_000) {
            formatted = `${(value / 1_000).toFixed(format.decimals ?? 1)}K`;
        } else {
            formatted = value.toFixed(format.decimals ?? 0);
        }
    } else {
        formatted = formatCount(value, { decimals: format.decimals ?? 0 });
    }

    if (format.prefix) formatted = `${format.prefix}${formatted}`;
    if (format.suffix) formatted = `${formatted}${format.suffix}`;

    if (format.type === 'currency') {
        const symbol =
            format.currency === 'USD' ? '$' : format.currency === 'EUR' ? '€' : currencySymbol();
        if (!format.prefix) formatted = `${symbol}${formatted}`;
    }

    if (format.type === 'percentage' && !format.suffix) {
        formatted = `${formatted}%`;
    }

    return formatted;
};

// =============================================================================
// TREND INDICATOR — unchanged from original
// =============================================================================
interface TrendIndicatorProps {
    trend: MetricTrend;
}

const TrendIndicator: React.FC<TrendIndicatorProps> = memo(({ trend }) => {
    const { direction, value, positiveIsGood = true } = trend;
    const isPositive = direction === 'up';
    const isGood = positiveIsGood ? isPositive : !isPositive;

    const color =
        direction === 'neutral'
            ? semanticColors.neutral
            : isGood
              ? semanticColors.positive
              : semanticColors.negative;

    const Icon =
        direction === 'up' ? TrendUp : direction === 'down' ? TrendDown : Minus;

    return (
        <Group style={{ gap: 4, color }}>
            <Icon size={14} weight="bold" />
            <Text style={{ fontSize: 12, fontWeight: 500, color }}>{value}</Text>
        </Group>
    );
});

TrendIndicator.displayName = 'TrendIndicator';

// =============================================================================
// PROPS
// =============================================================================
export interface MetricCardProps {
    instance: MetricCardInstance;
    /** Context tokens resolved into apiCalls at runtime, e.g. { vendorId: 'abc' } */
    context?: Record<string, string>;
    className?: string;
    onEditClick?: () => void;
}

// =============================================================================
// MAIN COMPONENT
// =============================================================================
const MetricCard: React.FC<MetricCardProps> = memo(
    ({ instance, context, className = '', onEditClick }) => {
        const { fields, isLoading, error } = useMetricCard(instance, context);

        const { captureContext, addToChat } = useWidgetContextCapture({
            componentName: 'MetricCard',
            label: instance.id,
        });

        // Capture context whenever the value loads
        useEffect(() => {
            if (fields.value !== null && fields.value !== undefined) {
                captureContext(
                    { metric_id: instance.id, api_calls: instance.apiCalls as unknown } as Record<string, unknown>,
                );
            }
        }, [fields.value, fields.label, instance.id, instance.apiCalls, captureContext]);

        // Selection state for Add to Chat
        const [isSelected, setIsSelected] = useState(false);
        const cardRef = useRef<HTMLDivElement>(null);

        useEffect(() => {
            if (!isSelected) return;
            const handleOutsideClick = (e: MouseEvent) => {
                if (cardRef.current && !cardRef.current.contains(e.target as Node)) {
                    setIsSelected(false);
                }
            };
            document.addEventListener('mousedown', handleOutsideClick);
            return () => document.removeEventListener('mousedown', handleOutsideClick);
        }, [isSelected]);

        const [editDialogOpen, setEditDialogOpen] = useState(false);
        const [isSaving, setIsSaving] = useState(false);

        const handleEditClick = useCallback(() => {
            if (onEditClick) {
                onEditClick();
            } else {
                setEditDialogOpen(true);
            }
        }, [onEditClick]);

        const handleEditSave = useCallback(async (data: unknown) => {
            setIsSaving(true);
            console.log('[MetricCard] Saving edits:', data);
            await new Promise((resolve) => setTimeout(resolve, 500));
            setIsSaving(false);
            setEditDialogOpen(false);
        }, []);

        const editDialogConfig: EditDialogConfig | null = useMemo(() => {
            if (!instance.isEditable) return null;
            return {
                entityId: instance.id,
                entityType: 'metric',
                entityLabel: fields.label ?? instance.id,
                reference: {
                    title: fields.label ?? instance.id,
                    currentValue: fields.value ?? undefined,
                    currentValueFormat:
                        instance.format.type === 'currency'
                            ? 'currency'
                            : instance.format.type === 'percentage'
                              ? 'percentage'
                              : 'number',
                },
                agentConfig: {
                    endpoint: '/api/v1/agent/metric-editor',
                    method: 'POST',
                    agentType: 'metric-editor',
                },
            };
        }, [instance, fields.label, fields.value]);

        const formattedValue = formatValue(fields.value, instance.format);

        return (
            <>
                <Box
                    ref={cardRef as React.RefObject<HTMLDivElement>}
                    className={`relative group ${className}`}
                    style={{
                        backgroundColor: 'white',
                        borderRadius: 12,
                        padding: `${spacing.md}px`,
                        boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                        border: isSelected ? '1px solid rgba(99,102,241,0.5)' : '1px solid transparent',
                        outline: isSelected ? '2px solid rgba(99,102,241,0.2)' : 'none',
                        outlineOffset: 2,
                        cursor: 'pointer',
                        transition: 'box-shadow 0.2s ease, border-color 0.15s ease, outline 0.15s ease',
                    }}
                    onClick={() => setIsSelected((v) => !v)}
                >
                    {/* Add to Chat chip — floats above the top-right corner of the selection ring */}
                    {isSelected && (
                        <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); addToChat(); setIsSelected(false); }}
                            style={{
                                position: 'absolute',
                                top: -24,
                                right: 0,
                                zIndex: 10,
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 3,
                                padding: '2px 7px',
                                fontSize: 10,
                                fontWeight: 500,
                                color: '#6366f1',
                                background: 'rgba(99,102,241,0.06)',
                                border: '1px solid rgba(99,102,241,0.22)',
                                borderRadius: 5,
                                cursor: 'pointer',
                                whiteSpace: 'nowrap',
                                boxShadow: '0 1px 4px rgba(99,102,241,0.12)',
                                lineHeight: 1.4,
                            }}
                        >
                            <ChatCircleDots size={10} weight="duotone" />
                            Add to Chat
                        </button>
                    )}

                    {/* Header row: Label + Edit button */}
                    <Group
                        className="justify-between items-start"
                        style={{ marginBottom: spacing.xs }}
                    >
                        <Text
                            className="uppercase tracking-wider text-slate-500"
                            style={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.05em' }}
                        >
                            {fields.label ?? '—'}
                        </Text>

                        {instance.isEditable && (
                            <Tooltip label="Edit metric" position="top">
                                <ActionIcon
                                    variant="subtle"
                                    color="gray"
                                    size="xs"
                                    onClick={handleEditClick}
                                    className="opacity-0 group-hover:opacity-100 transition-opacity"
                                >
                                    <PencilSimple size={12} weight="bold" />
                                </ActionIcon>
                            </Tooltip>
                        )}
                    </Group>

                    {/* Value */}
                    {isLoading ? (
                        <Skeleton height={36} width={100} radius={4} />
                    ) : error ? (
                        <Group style={{ gap: spacing.xs, color: semanticColors.negative }}>
                            <Warning size={20} weight="fill" />
                            <Text style={{ fontSize: 13 }}>Error loading</Text>
                        </Group>
                    ) : (
                        <Stack style={{ gap: 4 }}>
                            <Text
                                className="text-slate-900"
                                style={{
                                    fontSize: 34,
                                    fontWeight: 700,
                                    lineHeight: 1.1,
                                    letterSpacing: '-0.02em',
                                }}
                            >
                                {formattedValue}
                            </Text>

                            {fields.trend && <TrendIndicator trend={fields.trend} />}
                        </Stack>
                    )}

                    {/* Subtext */}
                    {fields.subtext && !error && !isLoading && (
                        <Text
                            className="text-slate-400"
                            style={{ fontSize: 12, lineHeight: 1.4, marginTop: spacing.xs }}
                        >
                            {fields.subtext}
                        </Text>
                    )}
                </Box>

                {instance.isEditable && editDialogConfig && (
                    <EditDialog
                        opened={editDialogOpen}
                        config={editDialogConfig}
                        onClose={() => setEditDialogOpen(false)}
                        onSave={handleEditSave}
                        isSaving={isSaving}
                    />
                )}
            </>
        );
    },
);

MetricCard.displayName = 'MetricCard';

export default MetricCard;
